import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { t, font, Panel, Button, Input, Badge, EmptyState } from "@realtyflows/ui";
import {
  listCommissionPlans,
  createCommissionPlan,
  updateCommissionPlan,
  quickbooksExportUrl,
} from "../api";

/**
 * CommissionPlans — V5 Feature 5.10 UI
 *
 * Team owner manages graduated commission split plans. Each plan has a
 * list of tiers: { min_gci_cents, agent_pct }. Agent's effective split
 * is determined by their YTD GCI.
 *
 * Also offers a QuickBooks CSV export of confirmed disbursements.
 */
export default function CommissionPlans({ user }) {
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // plan being edited (or null)
  const [showCreate, setShowCreate] = useState(false);
  const [draft, setDraft] = useState(blankPlan());
  const [exportRange, setExportRange] = useState({
    start: new Date(Date.now() - 30 * 86400000).toISOString().slice(0, 10),
    end: new Date().toISOString().slice(0, 10),
  });

  function blankPlan() {
    return {
      name: "",
      description: "",
      tiers: [
        { min_gci_cents: 0, agent_pct: 60 },
        { min_gci_cents: 5000000, agent_pct: 70 },
        { min_gci_cents: 10000000, agent_pct: 80 },
      ],
    };
  }

  useEffect(() => {
    listCommissionPlans()
      .then(setPlans)
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    if (!draft.name.trim()) { window.alert("Plan name is required"); return; }
    if (draft.tiers.length === 0) { window.alert("At least one tier is required"); return; }
    try {
      if (editing) {
        const updated = await updateCommissionPlan(editing.id, draft);
        setPlans(plans.map((p) => (p.id === updated.id ? updated : p)));
      } else {
        const created = await createCommissionPlan(draft);
        setPlans([...plans, created]);
      }
      setShowCreate(false); setEditing(null); setDraft(blankPlan());
    } catch (err) {
      window.alert(err.message || "Save failed");
    }
  };

  const handleEdit = (plan) => {
    setEditing(plan);
    setDraft({
      name: plan.name,
      description: plan.description || "",
      tiers: plan.tiers || [],
    });
    setShowCreate(true);
  };

  const handleTierChange = (idx, key, value) => {
    const tiers = [...draft.tiers];
    tiers[idx] = { ...tiers[idx], [key]: key === "min_gci_cents" ? Math.round(parseFloat(value || 0) * 100) : parseFloat(value || 0) };
    setDraft({ ...draft, tiers });
  };

  const addTier = () => {
    const lastTier = draft.tiers[draft.tiers.length - 1];
    setDraft({
      ...draft,
      tiers: [...draft.tiers, {
        min_gci_cents: (lastTier?.min_gci_cents || 0) + 5_000_000,
        agent_pct: Math.min(95, (lastTier?.agent_pct || 60) + 10),
      }],
    });
  };

  const removeTier = (idx) => {
    setDraft({ ...draft, tiers: draft.tiers.filter((_t, i) => i !== idx) });
  };

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 1000, margin: "0 auto" }}>
      <div style={{ marginBottom: 16 }}>
        <button onClick={() => navigate("/settings")} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: t.textMuted, fontFamily: font.body }}>
          ← Back to Settings
        </button>
      </div>

      <Panel padding={24} style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
          <div>
            <div style={{ fontSize: 20, fontWeight: 800, color: t.textPrimary, fontFamily: font.display }}>Commission Plans</div>
            <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4, fontFamily: font.body }}>
              Graduated split schedules. As agents earn more YTD GCI, they move to higher tiers automatically.
            </div>
          </div>
          <Button variant="primary" onClick={() => { setEditing(null); setDraft(blankPlan()); setShowCreate(true); }}>
            + New plan
          </Button>
        </div>
      </Panel>

      {/* Plan editor */}
      {showCreate && (
        <Panel padding={20} style={{ marginBottom: 16 }}>
          <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
            {editing ? `Edit "${editing.name}"` : "New commission plan"}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 }}>
            <Input placeholder="Plan name (e.g. Standard 60/40)" value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
            <Input placeholder="Description (optional)" value={draft.description} onChange={(e) => setDraft({ ...draft, description: e.target.value })} />
          </div>

          <div style={{ fontSize: 11, color: t.textMuted, fontWeight: 700, fontFamily: font.mono, letterSpacing: 0.5, textTransform: "uppercase", marginBottom: 8 }}>
            Tier Schedule (lowest YTD GCI to highest)
          </div>
          {draft.tiers.map((tier, idx) => (
            <div key={idx} style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: 8, marginBottom: 8 }}>
              <Input
                type="number"
                placeholder="Min YTD GCI ($)"
                value={(tier.min_gci_cents / 100).toString()}
                onChange={(e) => handleTierChange(idx, "min_gci_cents", e.target.value)}
              />
              <Input
                type="number"
                placeholder="Agent % (0-100)"
                value={String(tier.agent_pct)}
                onChange={(e) => handleTierChange(idx, "agent_pct", e.target.value)}
              />
              <Button variant="outline" size="sm" onClick={() => removeTier(idx)} disabled={draft.tiers.length === 1}>×</Button>
            </div>
          ))}
          <div style={{ marginBottom: 14 }}>
            <Button variant="outline" size="sm" onClick={addTier}>+ Add tier</Button>
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Button variant="primary" onClick={handleSave}>{editing ? "Save changes" : "Create plan"}</Button>
            <Button variant="outline" onClick={() => { setShowCreate(false); setEditing(null); }}>Cancel</Button>
          </div>
        </Panel>
      )}

      {/* Plans list */}
      <Panel padding={20} style={{ marginBottom: 16 }}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 14, fontFamily: font.display }}>
          Your plans
        </div>
        {loading ? (
          <div style={{ color: t.textMuted, fontFamily: font.body, fontSize: 12 }}>Loading...</div>
        ) : plans.length === 0 ? (
          <EmptyState title="No plans yet" sub="Create your first commission plan to define how splits work for your team." />
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
            {plans.map((plan) => (
              <div key={plan.id} style={{
                padding: 14, background: t.surfaceWarm,
                border: `1px solid ${t.border}`, borderRadius: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10, marginBottom: 8 }}>
                  <div>
                    <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.display }}>{plan.name}</div>
                    {plan.description && (
                      <div style={{ fontSize: 11, color: t.textMuted, marginTop: 2, fontFamily: font.body }}>{plan.description}</div>
                    )}
                  </div>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(plan)}>Edit</Button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {(plan.tiers || []).map((tier, i) => (
                    <Badge key={i} color={t.emerald}>
                      ${(tier.min_gci_cents / 100).toLocaleString()}+ → {tier.agent_pct}%/{(100 - tier.agent_pct)}%
                    </Badge>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </Panel>

      {/* QuickBooks export */}
      <Panel padding={20}>
        <div style={{ fontSize: 14, fontWeight: 700, color: t.textPrimary, marginBottom: 6, fontFamily: font.display }}>
          QuickBooks Export
        </div>
        <div style={{ fontSize: 12, color: t.textMuted, marginBottom: 14, fontFamily: font.body }}>
          Download a QuickBooks-compatible CSV of every confirmed disbursement in the selected period.
        </div>
        <div style={{ display: "flex", gap: 8, alignItems: "flex-end" }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: t.textSecondary, fontWeight: 600, marginBottom: 5, fontFamily: font.body }}>Start date</div>
            <Input type="date" value={exportRange.start} onChange={(e) => setExportRange({ ...exportRange, start: e.target.value })} />
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 11, color: t.textSecondary, fontWeight: 600, marginBottom: 5, fontFamily: font.body }}>End date</div>
            <Input type="date" value={exportRange.end} onChange={(e) => setExportRange({ ...exportRange, end: e.target.value })} />
          </div>
          <a
            href={`${quickbooksExportUrl(exportRange.start, exportRange.end)}&token=${localStorage.getItem("realtyflows_token") || ""}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ textDecoration: "none" }}
          >
            <Button variant="primary">Download CSV</Button>
          </a>
        </div>
        <div style={{ marginTop: 8, fontSize: 11, color: t.textMuted, fontFamily: font.body }}>
          Note: only "confirmed" disbursements appear in the export.
        </div>
      </Panel>
    </div>
  );
}
